import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from './auth/auth.service';
import { ArtistService } from './services/artist.service';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { PayeeService } from './services/payee.service';
import { ContractService } from './services/contract.service';
import { GoogleAdsService } from './services/google-ads.service';
import { MetaAdsService } from './services/meta-ads.service';
import { setLogLevel, LogLevel } from "@angular/fire";
import { NgClass, AsyncPipe, Location } from '@angular/common';
import { BaseHeaderComponent } from './components/shared/base-header/base-header.component';
import { IonSpinner, IonNote, IonContent, IonRouterOutlet } from "@ionic/angular/standalone";
import { addIcons } from "ionicons";
import { App } from '@capacitor/app';

@Component({
  standalone: true,
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    NgClass,
    AsyncPipe,
    BaseHeaderComponent,
    IonSpinner,
    IonNote,
    IonContent,
    IonRouterOutlet
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  auth = inject(AuthService);
  private router = inject(Router);
  artistService = inject(ArtistService);
  payeeService = inject(PayeeService);
  contractService = inject(ContractService);
  private googleAds = inject(GoogleAdsService);
  private metaAds = inject(MetaAdsService);
  private location = inject(Location);

  authSubscription: Subscription;
  contractSubscription: Subscription;
  loggedIn: boolean;

  constructor() {
    addIcons({
      'chat-bubble': 'assets/icon/chat-bubble.svg',
      'currency-usd': 'assets/icon/currency-usd.svg',
      'email': 'assets/icon/email.svg',
      // 'eye': 'assets/icon/eye.svg',
      'headphone': 'assets/icon/headphone.svg',
      'invoice-money': 'assets/icon/invoice-money.svg',
      'list': 'assets/icon/list.svg',
      'lock': 'assets/icon/lock.svg',
      'magnifying-glass-money': 'assets/icon/magnifying-glass-money.svg',
      'money-hand': 'assets/icon/money-hand.svg',
      'profile': 'assets/icon/profile.svg',
      'question-mark': 'assets/icon/question-mark.svg',
      'sad-face': 'assets/icon/sad-face.svg'
    });

  }

  async ngOnInit() {
    // App auto updater
    CapacitorUpdater.notifyAppReady();

    setLogLevel(LogLevel.SILENT);

    this.googleAds.init();
    this.metaAds.init();
    this.listenToEvents();
    this.setupDeepLinkHandler();
  }

  private setupDeepLinkHandler() {
    App.addListener('appUrlOpen', (data) => {
      console.log('App opened with URL:', data.url);
      // Deep link handling - currently just opening the app is sufficient
      // Future: can parse URL and navigate to specific pages if needed
    });
  }

  ngOnDestroy() {
    this.authSubscription?.unsubscribe();
    this.contractSubscription?.unsubscribe();
    App.removeAllListeners();
  }

  listenToEvents() {
    this.authSubscription = this.auth.user$.subscribe((user) => {
      const loggedIn = !!user;
      this.loggedIn = loggedIn;
      if (!loggedIn) {
        if (!this.isOnLoginRoute()) {
          this.router.navigate(['/login']);
        }
        this.contractService.setShowSpinner(false);
        return;
      }

      this.contractSubscription?.unsubscribe();
      this.contractSubscription = this.contractService.initializeContractCheck();
    });
  }

  signOut() {
    this.auth.signOut();
    this.loggedIn = false;
  }

  private isOnLoginRoute(): boolean {
    // Use Location.path() rather than router.url because at app boot the
    // router's initial navigation hasn't completed yet, so router.url returns
    // '/' even when the user landed directly on /login/signup-form.
    return this.location.path().startsWith('/login');
  }

}
